import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X, Sun, Moon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";

const LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/drops", label: "New Drops" },
  { to: "/collections", label: "Collections" },
  { to: "/run-club", label: "Run Club" },
  { to: "/about", label: "About" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const { theme, toggle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border bg-background/75 backdrop-blur-xl">
        <div className="flex h-16 items-center justify-between px-5 sm:px-10 lg:px-16">
          <div className="flex items-center gap-8">
            <Logo />
            <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 hover:text-signal ${
                      isActive ? "text-signal" : "text-muted-foreground"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Link
              to="/shop"
              data-testid="search-nav-button"
              aria-label="Search"
              className="p-2 text-muted-foreground transition-colors hover:text-foreground"
            >
              <Search className="h-[18px] w-[18px]" />
            </Link>
            <button
              onClick={toggle}
              data-testid="theme-toggle-button"
              aria-label="Toggle theme"
              className="p-2 text-muted-foreground transition-colors hover:text-foreground"
            >
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>
            <Link
              to="/account"
              data-testid="account-nav-button"
              aria-label="Account"
              className="hidden p-2 text-muted-foreground transition-colors hover:text-foreground sm:block"
            >
              <User className="h-[18px] w-[18px]" />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="cart-drawer-toggle"
              aria-label="Open cart"
              className="relative p-2 text-muted-foreground transition-colors hover:text-foreground"
            >
              <ShoppingBag className="h-[18px] w-[18px]" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center bg-signal px-1 font-mono text-[10px] font-bold text-white"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="p-2 text-foreground lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-background"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <Logo />
              <button
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="p-2"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-display text-5xl font-bold uppercase tracking-tight transition-colors hover:text-signal"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-10 flex gap-6 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground"
              >
                <Link to="/account" onClick={() => setMenuOpen(false)}>Account</Link>
                <Link to="/track-order" onClick={() => setMenuOpen(false)}>Track Order</Link>
                <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
